import React, { useEffect, useState } from "react";
import { CheckCircle, XCircle, Trash2, Pencil, X, Save } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUS_COLORS = {
  pending: "text-yellow-400 border-yellow-400/40",
  confirmed: "text-green-400 border-green-400/40",
  cancelled: "text-red-400 border-red-400/40",
};

const TIME_SLOTS = [
  "11:30", "12:00", "12:30", "13:00", "13:30",
  "17:30", "18:00", "18:30", "19:00", "19:30", "20:00", "20:30",
];

export default function ReservationsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/reservations").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.put(`/reservations/${id}/status`, null, { params: { status } });
    toast.success(`Marked ${status}`);
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this reservation?")) return;
    await api.delete(`/reservations/${id}`);
    load();
  };

  return (
    <div data-testid="reservations-admin">
      <h1 className="font-serif-display text-4xl text-gold-gradient mb-2">Reservations</h1>
      <p className="text-sm text-[#D1C7C9] mb-6">Manage all booking requests · edit date/time/details after the customer books</p>

      <div className="bg-[#1A1A1A] border border-[#333] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-xs uppercase tracking-widest text-[#D1C7C9]">
            <tr>
              <th className="text-left p-3">Guest</th>
              <th className="text-left p-3">Date / Time</th>
              <th className="text-left p-3">Party</th>
              <th className="text-left p-3">Contact</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-t border-[#333]" data-testid={`reservation-row-${r.id}`}>
                <td className="p-3 font-medium">{r.name}</td>
                <td className="p-3 text-[#D1C7C9]">{r.date} · {r.time}</td>
                <td className="p-3">{r.party_size}</td>
                <td className="p-3 text-[#D1C7C9] text-xs">{r.phone}<br/>{r.email}</td>
                <td className="p-3">
                  <span className={`text-[10px] tracking-widest uppercase border px-2 py-0.5 rounded ${STATUS_COLORS[r.status]}`}>{r.status}</span>
                </td>
                <td className="p-3 text-right space-x-2">
                  <button onClick={() => setEditing({ ...r })} className="text-blue-400 hover:text-blue-300" title="Edit date/time/details" data-testid={`edit-res-${r.id}`}><Pencil size={16} /></button>
                  {r.status !== "confirmed" && (
                    <button onClick={() => setStatus(r.id, "confirmed")} className="text-green-400 hover:text-green-300" data-testid={`confirm-${r.id}`}><CheckCircle size={16} /></button>
                  )}
                  {r.status !== "cancelled" && (
                    <button onClick={() => setStatus(r.id, "cancelled")} className="text-yellow-400 hover:text-yellow-300" data-testid={`cancel-${r.id}`}><XCircle size={16} /></button>
                  )}
                  <button onClick={() => del(r.id)} className="text-red-400 hover:text-red-300" data-testid={`delete-res-${r.id}`}><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-[#D1C7C9]">No reservations yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditReservationModal
          reservation={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function EditReservationModal({ reservation, onClose, onSaved }) {
  const [form, setForm] = useState(reservation);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        email: form.email || "",
        date: form.date,
        time: form.time,
        party_size: parseInt(form.party_size, 10),
        special_requests: form.special_requests || "",
        status: form.status,
      };
      await api.put(`/reservations/${reservation.id}`, payload);
      toast.success("Reservation updated");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-reservation-modal">
      <div className="bg-[#1A1A1A] border border-[#333] rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif-display text-2xl text-gold-gradient">Edit Reservation</h2>
          <button onClick={onClose} className="text-[#D1C7C9] hover:text-white" data-testid="close-edit-modal"><X size={20} /></button>
        </div>

        <div className="space-y-3">
          <ModalField label="Guest Name" value={form.name} onChange={set("name")} testId="edit-name" />
          <ModalField label="Phone" value={form.phone} onChange={set("phone")} testId="edit-phone" />
          <ModalField label="Email" value={form.email || ""} onChange={set("email")} testId="edit-email" />

          <div className="grid grid-cols-2 gap-3">
            <ModalField label="Date (YYYY-MM-DD)" value={form.date} onChange={set("date")} type="date" testId="edit-date" />
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-[#D1C7C9] block mb-1">Time</span>
              <select value={form.time} onChange={set("time")} data-testid="edit-time"
                className="w-full bg-[#0A0A0A] border border-[#333] rounded-md px-3 py-2 text-sm text-[#FDFBF7] outline-none focus:border-[#E69138]">
                {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
                {!TIME_SLOTS.includes(form.time) && form.time && <option value={form.time}>{form.time} (custom)</option>}
              </select>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <ModalField label="Party Size" value={form.party_size} onChange={set("party_size")} type="number" testId="edit-party" />
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-[#D1C7C9] block mb-1">Status</span>
              <select value={form.status} onChange={set("status")} data-testid="edit-status"
                className="w-full bg-[#0A0A0A] border border-[#333] rounded-md px-3 py-2 text-sm text-[#FDFBF7] outline-none focus:border-[#E69138]">
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </label>
          </div>

          <label className="block">
            <span className="text-xs uppercase tracking-widest text-[#D1C7C9] block mb-1">Special Requests</span>
            <textarea rows={3} value={form.special_requests || ""} onChange={set("special_requests")} data-testid="edit-requests"
              className="w-full bg-[#0A0A0A] border border-[#333] rounded-md px-3 py-2 text-sm text-[#FDFBF7] outline-none focus:border-[#E69138] resize-none" />
          </label>
        </div>

        <div className="flex gap-2 justify-end mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#D1C7C9] hover:text-white" data-testid="cancel-edit-btn">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-saffron disabled:opacity-50" data-testid="save-edit-btn">
            <Save size={14} /> {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalField({ label, value, onChange, type = "text", testId }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-[#D1C7C9] block mb-1">{label}</span>
      <input type={type} value={value || ""} onChange={onChange} data-testid={testId}
        className="w-full bg-[#0A0A0A] border border-[#333] rounded-md px-3 py-2 text-sm text-[#FDFBF7] outline-none focus:border-[#E69138]" />
    </label>
  );
}
